<script setup>
import { router, useForm } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

const props = defineProps({
  notebooks: { type: Array, required: true },
  privilegeHolderCount: { type: Number, required: true },
})

const { t } = useI18n()
const toast = useToast()
const confirm = useConfirm()

// Static for now — only the evaluation view embeds a notebook button. New
// placements get an entry here + in the server-side whitelist (Notebook::PLACEMENTS).
const placementOptions = [
  { value: 'evaluation', labelKey: 'messages.admin.notebooks.placement.evaluation' },
]

// null = form hidden; 'create' | 'edit' controls the card heading.
const formMode = ref(null)

const form = useForm({
  id: null,
  title: '',
  short_title: '',
  url: '',
  description: '',
  sample_prompt: '',
  placement: 'evaluation',
  is_active: true,
})

// Only one notebook per placement may be active; the server switches the
// others off, and the success toast should say so.
const willDeactivateOthers = (id, placement, isActive) =>
  isActive && props.notebooks.some(n => n.is_active && n.id !== id && n.placement === placement)

const savedToast = deactivatedOthers => {
  toast.add({
    severity: 'success',
    summary: t('messages.admin.notebooks.toast_saved'),
    detail: deactivatedOthers ? t('messages.admin.notebooks.toast_deactivated_others') : undefined,
    life: deactivatedOthers ? 4000 : 2000,
  })
}

const openCreate = () => {
  form.clearErrors()
  form.reset()
  formMode.value = 'create'
}

const openEdit = notebook => {
  form.clearErrors()
  form.id = notebook.id
  form.title = notebook.title
  form.short_title = notebook.short_title
  form.url = notebook.url
  form.description = notebook.description ?? ''
  form.sample_prompt = notebook.sample_prompt
  form.placement = notebook.placement
  form.is_active = notebook.is_active
  formMode.value = 'edit'
}

const closeForm = () => {
  formMode.value = null
  form.clearErrors()
  form.reset()
}

const submit = () => {
  const deactivatedOthers = willDeactivateOthers(form.id, form.placement, form.is_active)

  const options = {
    preserveScroll: true,
    onSuccess: () => {
      savedToast(deactivatedOthers)
      closeForm()
    },
    onError: () => {
      toast.add({ severity: 'error', summary: t('messages.admin.notebooks.toast_error'), life: 3000 })
    },
  }

  if (form.id) {
    form.put(route('admin.notebook.update', form.id), options)
  } else {
    form.post(route('admin.notebook.store'), options)
  }
}

// Per-row in-flight guard so a ToggleSwitch can't double-fire mid-request.
const saving = ref(null)

const toggleActive = (notebook, isActive) => {
  saving.value = notebook.id
  const deactivatedOthers = willDeactivateOthers(notebook.id, notebook.placement, isActive)

  router.put(
    route('admin.notebook.update', notebook.id),
    {
      title: notebook.title,
      short_title: notebook.short_title,
      url: notebook.url,
      description: notebook.description,
      sample_prompt: notebook.sample_prompt,
      placement: notebook.placement,
      is_active: isActive,
    },
    {
      preserveScroll: true,
      onSuccess: () => {
        savedToast(deactivatedOthers)
      },
      onError: () => {
        toast.add({ severity: 'error', summary: t('messages.admin.notebooks.toast_error'), life: 3000 })
      },
      onFinish: () => {
        saving.value = null
      },
    },
  )
}

const confirmDelete = notebook => {
  confirm.require({
    header: t('messages.admin.notebooks.delete_confirm_header'),
    message: t('messages.admin.notebooks.delete_confirm_message', { title: notebook.title }),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: t('messages.admin.notebooks.form.cancel'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('messages.admin.notebooks.delete'), severity: 'danger' },
    accept: () => {
      router.delete(route('admin.notebook.destroy', notebook.id), {
        preserveScroll: true,
        onSuccess: () => {
          if (form.id === notebook.id) {
            closeForm()
          }
          toast.add({ severity: 'success', summary: t('messages.admin.notebooks.toast_deleted'), life: 2000 })
        },
        onError: () => {
          toast.add({ severity: 'error', summary: t('messages.admin.notebooks.toast_error'), life: 3000 })
        },
      })
    },
  })
}
</script>

<template>
  <div class="max-w-5xl space-y-4 py-4">
    <div class="flex items-center justify-between gap-4">
      <h2 class="text-xl font-medium text-gray-800">
        {{ $t('messages.admin.notebooks.title') }}
      </h2>
      <Button data-testid="notebook-add-button" icon="pi pi-plus" :label="$t('messages.admin.notebooks.add')" @click="openCreate" />
    </div>

    <!-- The feature is privilege-gated: configuring a notebook here does NOT
         make the button visible by itself (RED-291 round-3 requirement). -->
    <Message
      severity="info"
      size="small"
      :closable="false"
      :dt="{ text: { fontWeight: 400 } }"
      data-testid="notebook-privilege-banner"
    >
      {{ $t('messages.admin.notebooks.privilege_banner') }}
      {{ $t('messages.admin.notebooks.privilege_banner_count', { count: privilegeHolderCount }) }}
    </Message>

    <DataTable
      :value="notebooks"
      data-testid="notebook-table"
      :row-class="row => (row.is_active ? '' : 'text-gray-400')"
    >
      <template #empty>
        <div class="py-3 text-sm text-gray-500">{{ $t('messages.admin.notebooks.empty') }}</div>
      </template>
      <Column field="title" :header="$t('messages.admin.notebooks.columns.title')">
        <template #body="{ data }">
          <span class="font-medium">{{ data.title }}</span>
        </template>
      </Column>
      <Column field="short_title" :header="$t('messages.admin.notebooks.columns.short_title')" />
      <Column field="url" :header="$t('messages.admin.notebooks.columns.url')">
        <template #body="{ data }">
          <span class="block max-w-56 truncate font-mono text-xs text-gray-500">{{ data.url }}</span>
        </template>
      </Column>
      <Column field="placement" :header="$t('messages.admin.notebooks.columns.placement')">
        <template #body="{ data }">
          {{ $t(`messages.admin.notebooks.placement.${data.placement}`) }}
        </template>
      </Column>
      <Column field="is_active" :header="$t('messages.admin.notebooks.columns.active')">
        <template #body="{ data }">
          <ToggleSwitch
            :model-value="data.is_active"
            :disabled="saving === data.id"
            :data-testid="`notebook-active-toggle-${data.id}`"
            @update:model-value="val => toggleActive(data, val)"
          />
        </template>
      </Column>
      <Column>
        <template #body="{ data }">
          <div class="flex justify-end gap-1">
            <Button
              icon="pi pi-pencil"
              text
              severity="secondary"
              :data-testid="`notebook-edit-${data.id}`"
              @click="openEdit(data)"
            />
            <Button
              icon="pi pi-trash"
              text
              severity="danger"
              :data-testid="`notebook-delete-${data.id}`"
              @click="confirmDelete(data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <div v-if="formMode" class="rounded-xl border border-gray-200 bg-white p-6 shadow-xs" data-testid="notebook-form">
      <h3 class="mb-5 text-lg font-medium text-gray-800">
        {{ formMode === 'edit' ? $t('messages.admin.notebooks.edit_title') : $t('messages.admin.notebooks.create_title') }}
      </h3>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label for="notebook-title" class="mb-1 block text-sm font-medium text-gray-600">
            {{ $t('messages.admin.notebooks.form.title') }}
          </label>
          <InputText id="notebook-title" v-model="form.title" class="w-full" data-testid="notebook-form-title" />
          <small v-if="form.errors.title" class="text-red-600">{{ form.errors.title }}</small>
        </div>
        <div>
          <label for="notebook-short-title" class="mb-1 block text-sm font-medium text-gray-600">
            {{ $t('messages.admin.notebooks.form.short_title') }}
          </label>
          <InputText id="notebook-short-title" v-model="form.short_title" class="w-full" data-testid="notebook-form-short-title" />
          <small v-if="form.errors.short_title" class="text-red-600">{{ form.errors.short_title }}</small>
        </div>
        <div>
          <label for="notebook-placement" class="mb-1 block text-sm font-medium text-gray-600">
            {{ $t('messages.admin.notebooks.columns.placement') }}
          </label>
          <Select
            v-model="form.placement"
            input-id="notebook-placement"
            :options="placementOptions"
            :option-label="option => $t(option.labelKey)"
            option-value="value"
            class="w-full"
            data-testid="notebook-form-placement"
          />
          <small v-if="form.errors.placement" class="text-red-600">{{ form.errors.placement }}</small>
        </div>
        <div class="md:col-span-2">
          <label for="notebook-url" class="mb-1 block text-sm font-medium text-gray-600">
            {{ $t('messages.admin.notebooks.form.url') }}
          </label>
          <InputText id="notebook-url" v-model="form.url" class="w-full font-mono" data-testid="notebook-form-url" />
          <small v-if="form.errors.url" class="text-red-600" data-testid="notebook-form-url-error">{{ form.errors.url }}</small>
        </div>
        <div class="md:col-span-2">
          <label for="notebook-description" class="mb-1 block text-sm font-medium text-gray-600">
            {{ $t('messages.admin.notebooks.form.description') }}
          </label>
          <Textarea id="notebook-description" v-model="form.description" class="w-full" rows="2" auto-resize data-testid="notebook-form-description" />
          <small v-if="form.errors.description" class="text-red-600">{{ form.errors.description }}</small>
        </div>
        <div class="md:col-span-2">
          <label for="notebook-sample-prompt" class="mb-1 block text-sm font-medium text-gray-600">
            {{ $t('messages.admin.notebooks.form.sample_prompt') }}
          </label>
          <Textarea id="notebook-sample-prompt" v-model="form.sample_prompt" class="w-full font-mono" rows="3" auto-resize data-testid="notebook-form-sample-prompt" />
          <small v-if="form.errors.sample_prompt" class="text-red-600">{{ form.errors.sample_prompt }}</small>
        </div>
      </div>

      <div class="mt-6 flex justify-end gap-3">
        <Button
          :label="$t('messages.admin.notebooks.form.cancel')"
          text
          severity="secondary"
          data-testid="notebook-form-cancel"
          @click="closeForm"
        />
        <Button
          :label="$t('messages.admin.notebooks.form.save')"
          :disabled="form.processing"
          data-testid="notebook-form-save"
          @click="submit"
        />
      </div>
    </div>
  </div>
</template>
